import * as THREE from "three";
import type { ShopRow } from "../maps";
import { WALL_SIDES } from "./beach";
import { mergeStatic } from "./merge";
import { COMING_SOON, INTERIORS, interiorMaterials, paperedWindow, ROOM_H, roomDepth, shopInterior, type InteriorMaterials } from "./shop-interiors";
import { atlasCell, COMING_SOON_CELL, SHOPS, shopMaterials, SIGN_H, SIGN_W } from "./shop-signs";
import { localSpan } from "./street-end";
import { toon } from "./toon";

// Shopfronts: a street spot's buildings (#115, #152, #186), each one shop
// with its sign, awning and, for some, a lit room you can see into. The
// street map draws them along its north and south sides (`buildStreet`);
// a shop row (#240, `look.shopRows`) draws a stretch of any wall as them.

/** Adds a mesh to the row at (x, y, z) and returns it; `cast` says whether it casts a shadow. */
export type AddMesh = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number, cast?: boolean) => THREE.Mesh;

/** The materials every front on a street or row shares, so the merge keeps them to a few draw calls. */
export interface FrontMaterials {
  /** The upper floors' windows, the shopfront's dark glass, and the trim (sill, fascia, cornice, sign frame). */
  windows: THREE.Material;
  shop: THREE.Material;
  trim: THREE.Material;
  /** The sign atlas's face and each shop's awning. */
  shops: ReturnType<typeof shopMaterials>;
  room: InteriorMaterials;
}

export function frontMaterials(): FrontMaterials {
  return {
    windows: toon("#9fb4c2", { roughness: 0.2, metalness: 0.3, emissive: "#3b4f5e", emissiveIntensity: 0.3 }, "glass"),
    shop: toon("#35434d", { roughness: 0.15, metalness: 0.2, emissive: "#6b5a3a", emissiveIntensity: 0.25 }, "glass"),
    trim: toon("#ddd6c9", { roughness: 0.8 }, "concrete"),
    shops: shopMaterials(),
    room: interiorMaterials(),
  };
}

/** One building's front: its west (low) end `x`, `w` along, `h` tall, `d` deep. */
export interface Front {
  x: number;
  w: number;
  h: number;
  d: number;
  /** Its shop, an index into `SHOPS`. */
  shop: number;
}

/**
 * One building with its shop, its front on the line z = `face` and the
 * building going back from it toward `sz` (-1 or 1), running along x: the
 * body in `wallMat`, a band of windows per floor and a cornice, and at street
 * level the shopfront (the glass, or a room behind clear glass for a shop in
 * `INTERIORS`, or papered over for the empty unit), its awning and its sign.
 */
export function shopFront(add: AddMesh, around: THREE.Object3D, m: FrontMaterials, f: Front, face: number, sz: number, wallMat: THREE.Material): void {
  const { x, w, h, d, shop: k } = f;
  const { trim, room, shops } = m;
  const cx = x + w / 2;
  const cz = face + (sz * d) / 2;
  const inside = INTERIORS[SHOPS[k]!.name];
  const empty = SHOPS[k]!.name === COMING_SOON;
  // A shop you can see into (#186) leaves a room behind its glass: the
  // building above it, behind it and either side of it, and the room's own walls and things.
  const gw = w * 0.8;
  if (inside) {
    const rd = roomDepth(inside);
    add(new THREE.BoxGeometry(w - 0.3, h - ROOM_H, d), wallMat, cx, ROOM_H + (h - ROOM_H) / 2, cz, false);
    add(new THREE.BoxGeometry(w - 0.3, ROOM_H, d - rd), wallMat, cx, ROOM_H / 2, face + sz * (rd + (d - rd) / 2), false);
    const pw = (w - 0.3 - gw) / 2;
    for (const s of [-1, 1]) add(new THREE.BoxGeometry(pw, ROOM_H, rd), wallMat, cx + s * (gw / 2 + pw / 2), ROOM_H / 2, face + (sz * rd) / 2, false);
    add(new THREE.BoxGeometry(gw, 0.25, 0.14), trim, cx, 0.125, face, false);
    // A wide front (the coffee shop's two units, #186 round 3) is glazed in panes about 4 m wide.
    const panes = (SHOPS[k]!.span ?? 1) > 1 ? Math.round(gw / 4) : 1;
    for (let i = 1; i < panes; i++) add(new THREE.BoxGeometry(0.14, 2.6, 0.18), trim, cx - gw / 2 + (gw * i) / panes, 1.55, face, false);
    for (const p of shopInterior(inside, gw, room)) {
      p.position.set(cx + p.position.x, p.position.y, face + sz * p.position.z);
      p.receiveShadow = true;
      around.add(p);
    }
  } else {
    const body = add(new THREE.BoxGeometry(w - 0.3, h, d), wallMat, cx, h / 2, cz);
    body.castShadow = false;
  }
  // Windows: a band per floor above the shopfront, a cornice on top.
  const floors = Math.floor((h - 4.5) / 3.2);
  for (let fl = 0; fl < floors; fl++) add(new THREE.BoxGeometry(w * 0.78, 1.5, 0.12), m.windows, cx, 5 + fl * 3.2 + 0.75, face, false);
  add(new THREE.BoxGeometry(w - 0.3, 0.5, 0.6), trim, cx, h - 0.25, face - sz * 0.25, false);
  // The shopfront: glass behind a trim band, the shop's awning, and its sign board above.
  if (empty) {
    // An empty unit (#186): brown paper over the glass, no awning.
    for (const p of paperedWindow(gw, 2.6, room)) {
      p.position.set(cx + p.position.x, 0.25 + p.position.y, face - sz * p.position.z);
      around.add(p);
    }
  } else add(new THREE.BoxGeometry(gw, 2.6, 0.12), inside ? room.glass : m.shop, cx, 1.55, face, false);
  add(new THREE.BoxGeometry(w - 0.3, 0.45, 0.3), trim, cx, 3.2, face - sz * 0.1, false);
  if (!empty) add(new THREE.BoxGeometry(w * 0.6, 0.12, 1.2), shops.awning(SHOPS[k]!), cx, 3.05, face - sz * 0.6);
  add(new THREE.BoxGeometry(SIGN_W + 0.2, SIGN_H + 0.2, 0.12), trim, cx, 4.15, face - sz * 0.08, false);
  // The board shows this shop's cell of the sign atlas.
  const plane = new THREE.PlaneGeometry(SIGN_W, SIGN_H);
  const [u0, v0, u1, v1] = atlasCell(empty ? COMING_SOON_CELL : k);
  const uv = plane.getAttribute("uv");
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) ? u1 : u0, uv.getY(i) ? v1 : v0);
  const board = add(plane, shops.face, cx, 4.15, face - sz * 0.15, false);
  // Facing out into the street.
  if (sz > 0) board.rotation.y = Math.PI;
}

/** How deep a shop row's buildings go back from the fence (m): what the beach town behind leaves room for. */
export const SHOP_ROW_DEPTH = 14;

/** Facade colors along a shop row: the beach town's warm render and brick. */
const ROW_COLORS = ["#d9a07a", "#e8d5b5", "#b5836a", "#f0c9a0", "#c8b89c", "#a8765c"];

/**
 * The fronts along a shop row, low end first, in its side's frame (local x
 * along the side, as `localSpan` gives it): its shops' units shared out
 * evenly across the stretch, a two-unit shop (the coffee shop) twice as
 * wide. A name that isn't one of `SHOPS` is left out (`shop-row.test.ts`
 * checks every map's are).
 */
export function rowFronts(row: ShopRow): Front[] {
  const ks = row.shops.map((n) => SHOPS.findIndex((s) => s.name === n)).filter((k) => k >= 0);
  // Listed from `from` to `to`; on the north and east sides local x runs the other way.
  const [x0, x1] = localSpan(row.side, row.from, row.to);
  const order = x0 === row.from ? ks : [...ks].reverse();
  const units = order.reduce((n, k) => n + (SHOPS[k]!.span ?? 1), 0);
  const unit = (x1 - x0) / Math.max(1, units);
  const out: Front[] = [];
  let x = x0;
  order.forEach((k, i) => {
    const w = unit * (SHOPS[k]!.span ?? 1);
    out.push({ x, w, h: 9 + ((i * 5) % 7), d: SHOP_ROW_DEPTH, shop: k });
    x += w;
  });
  return out;
}

/**
 * One shop row (#240), `fence` out from the middle: built along local x with
 * the buildings going back along +z (the south side's frame, their fronts on
 * the fence line facing the park) and turned to its side. Drawn only.
 */
export function buildShopRow(row: ShopRow, fence: number): THREE.Object3D {
  const root = new THREE.Group();
  root.rotation.y = (WALL_SIDES.indexOf(row.side) * Math.PI) / 2;
  const add: AddMesh = (geo, mat, x, y, z, cast = true) => {
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, y, z);
    mesh.castShadow = cast;
    mesh.receiveShadow = true;
    root.add(mesh);
    return mesh;
  };
  const m = frontMaterials();
  const walls = ROW_COLORS.map((c) => toon(c, { roughness: 0.85 }, "concrete"));
  rowFronts(row).forEach((f, i) => shopFront(add, root, m, f, fence, 1, walls[i % walls.length]!));
  return mergeStatic(root);
}
